<template>
<div class="fb-root" :style="themePageStyle">

  <!-- ===================== TOP BAR ===================== -->
  <nav class="fb-topbar">
    <div class="fb-tb-left">
      <button class="fb-hamburger d-md-none" @click="drawerOpen=true" title="Menu"><i class="mdi mdi-menu"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" class="fb-logo" /></a>
      <div class="fb-search" @click.stop>
        <i class="mdi mdi-magnify"></i>
        <input v-model="searchQ" @input="onSearch" @focus="searchOpen=true" placeholder="Search farmers" />
        <div v-if="searchOpen && (searchBusy || searchResults.length || searchQ.length>=2)" class="fb-drop fb-search-drop">
          <div v-if="searchBusy" class="fb-drop-empty"><span class="spinner-border spinner-border-sm"></span></div>
          <div v-else-if="!searchResults.length" class="fb-drop-empty">No farmers found</div>
          <div v-for="u in searchResults" :key="u.id" class="fb-search-item">
            <div class="fb-search-click" @click="goProfile(u.id)">
              <user-avatar :src="u.avatar_url" :name="u.name" size="sm" />
              <span class="fb-search-name">{{ u.name || 'A farmer' }}</span>
            </div>
            <button class="fb-mini-follow" :class="{done:u._followed}" @click="followFromSearch(u)">{{ u._followed ? 'Following' : 'Follow' }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="fb-tb-center">
      <button class="fb-nav active" title="Home"><i class="mdi mdi-home"></i></button>
      <button class="fb-nav" title="Marketplace" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline"></i></button>
      <button class="fb-nav" title="Learning Hub" @click="$router.push('/learn')"><i class="mdi mdi-play-box-outline"></i></button>
      <button class="fb-nav" title="Events &amp; Market Days" @click="$router.push('/events')"><i class="mdi mdi-calendar-star"></i></button>
      <button class="fb-nav" title="Vets" @click="$router.push('/vets')"><i class="mdi mdi-medical-bag"></i></button>
    </div>

    <div class="fb-tb-right">
      <!-- Inbox -->
      <div class="fb-iconwrap" @click.stop>
        <button class="fb-circle" title="Messages" @click="toggleInbox"><i class="mdi mdi-facebook-messenger"></i><span v-if="inboxUnread>0" class="fb-badge">{{ inboxUnread>9?'9+':inboxUnread }}</span></button>
        <div v-if="inboxOpen" class="fb-drop fb-drop-right">
          <div class="fb-drop-head"><span>Chats</span><a href="javascript:void(0)" @click="$router.push('/messages')">See all</a></div>
          <div v-if="inboxLoading" class="fb-drop-empty"><span class="spinner-border spinner-border-sm"></span></div>
          <div v-else-if="!convos.length" class="fb-drop-empty">No conversations yet</div>
          <div v-for="c in convos" :key="c.id" class="fb-drop-item" :class="{unread:Number(c.unread_count)>0}" @click="openConvo(c)">
            <user-avatar :src="c.other_avatar" :name="c.other_name" size="md" />
            <div class="fb-drop-item-body">
              <div class="fb-drop-item-title">{{ c.other_name || 'Conversation' }}</div>
              <div class="fb-drop-item-sub">{{ c.last_message_preview || 'Tap to open' }}</div>
            </div>
            <span v-if="Number(c.unread_count)>0" class="fb-dot"></span>
          </div>
        </div>
      </div>

      <!-- Notifications -->
      <div class="fb-iconwrap" @click.stop>
        <button class="fb-circle" title="Notifications" @click="toggleNotif"><i class="mdi mdi-bell"></i><span v-if="notifUnread>0" class="fb-badge">{{ notifUnread>9?'9+':notifUnread }}</span></button>
        <div v-if="notifOpen" class="fb-drop fb-drop-right">
          <div class="fb-drop-head"><span>Notifications</span><a href="javascript:void(0)" @click="markAllNotifs">Mark all read</a></div>
          <div v-if="notifLoading" class="fb-drop-empty"><span class="spinner-border spinner-border-sm"></span></div>
          <div v-else-if="!notifs.length" class="fb-drop-empty">You're all caught up</div>
          <div v-for="n in notifs" :key="n.id" class="fb-drop-item" :class="{unread:Number(n.is_read)===0}" @click="openNotif(n)">
            <div class="fb-notif-icon"><i class="mdi" :class="notifIcon(n.category)"></i></div>
            <div class="fb-drop-item-body">
              <div class="fb-drop-item-title">{{ n.title }}</div>
              <div class="fb-drop-item-sub">{{ n.body }}</div>
              <div class="fb-drop-item-time">{{ relTime(n.created_at) }}</div>
            </div>
            <span v-if="Number(n.is_read)===0" class="fb-dot"></span>
          </div>
        </div>
      </div>

      <button class="fb-circle fb-avatar-btn" title="My account" @click="$router.push('/dashboard')">
        <user-avatar :src="myAvatar" :name="myName" size="sm" />
      </button>
    </div>
  </nav>

  <!-- ===== MOBILE DRAWER (hamburger menu) — shows full left-rail nav on phones ===== -->
  <div v-if="drawerOpen" class="fb-drawer-backdrop" @click="drawerOpen=false"></div>
  <aside class="fb-drawer" :class="{open:drawerOpen}" @click.stop>
    <div class="fb-drawer-head">
      <span class="fw-bold" style="font-size:15px;">Menu</span>
      <button class="fb-drawer-close" @click="drawerOpen=false"><i class="mdi mdi-close"></i></button>
    </div>
    <div class="fb-drawer-body">
      <div class="fb-left-item" @click="$router.push('/dashboard'); drawerOpen=false">
        <user-avatar :src="myAvatar" :name="myName" size="sm" />
        <span>{{ myName }}</span>
      </div>
      <div class="fb-left-item" @click="$router.push('/messages'); drawerOpen=false"><i class="mdi mdi-message-text fb-li-msg"></i><span>Messages</span></div>
      <div class="fb-left-item" @click="$router.push('/notifications'); drawerOpen=false"><i class="mdi mdi-bell fb-li-bell"></i><span>Notifications</span></div>
      <div class="fb-left-item" :class="{active:feedScope==='following'}" @click="feedScope='following';loadFeed();drawerOpen=false"><i class="mdi mdi-account-heart fb-li-follow"></i><span>Following</span></div>
      <div class="fb-left-item" @click="$router.push('/marketplace'); drawerOpen=false"><i class="mdi mdi-storefront fb-li-shop"></i><span>Marketplace</span></div>
      <div class="fb-left-item" @click="$router.push('/learn'); drawerOpen=false"><i class="mdi mdi-book-open-variant fb-li-learn"></i><span>Learning Hub</span></div>
      <div class="fb-left-item" @click="$router.push('/vets'); drawerOpen=false"><i class="mdi mdi-medical-bag fb-li-vet"></i><span>Vets</span></div>
      <div class="fb-left-item" @click="$router.push('/events'); drawerOpen=false"><i class="mdi mdi-calendar-star fb-li-evt"></i><span>Events &amp; Market Days</span></div>
      <div class="fb-left-sep"></div>
      <div class="fb-left-head">Topics</div>
      <div v-for="c in categories" :key="'d-'+c.key" class="fb-left-item small" :class="{active:filter.category===c.key}" @click="toggleCategory(c.key); drawerOpen=false">
        <i class="mdi" :class="c.icon"></i><span>{{ c.label }}</span>
      </div>
      <div class="fb-left-foot">Flockr · Farmers Community</div>
    </div>
  </aside>

  <!-- ===================== SHELL ===================== -->
  <div class="fb-shell">

    <!-- LEFT RAIL -->
    <aside class="fb-left">
      <div class="fb-left-item" @click="$router.push('/dashboard')">
        <user-avatar :src="myAvatar" :name="myName" size="sm" />
        <span>{{ myName }}</span>
      </div>
      <div class="fb-left-item" @click="$router.push('/messages')"><i class="mdi mdi-message-text fb-li-msg"></i><span>Messages</span></div>
      <div class="fb-left-item" @click="$router.push('/notifications')"><i class="mdi mdi-bell fb-li-bell"></i><span>Notifications</span></div>
      <div class="fb-left-item" :class="{active:feedScope==='following'}" @click="feedScope='following';loadFeed()"><i class="mdi mdi-account-heart fb-li-follow"></i><span>Following</span></div>
      <div class="fb-left-item" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront fb-li-shop"></i><span>Marketplace</span></div>
      <div class="fb-left-item" @click="$router.push('/learn')"><i class="mdi mdi-book-open-variant fb-li-learn"></i><span>Learning Hub</span></div>
      <div class="fb-left-item" @click="$router.push('/vets')"><i class="mdi mdi-medical-bag fb-li-vet"></i><span>Vets</span></div>
      <div class="fb-left-item" @click="$router.push('/events')"><i class="mdi mdi-calendar-star fb-li-evt"></i><span>Events &amp; Market Days</span></div>

      <div class="fb-left-sep"></div>
      <div class="fb-left-head">Topics</div>
      <div v-for="c in categories" :key="c.key" class="fb-left-item small" :class="{active:filter.category===c.key}" @click="toggleCategory(c.key)">
        <i class="mdi" :class="c.icon"></i><span>{{ c.label }}</span>
      </div>
      <div class="fb-left-foot">Flockr · Farmers Community</div>
    </aside>

    <!-- CENTER -->
    <main class="fb-center">

      <!-- STORIES -->
      <div class="fb-stories">
        <div class="fb-story fb-story-create" @click="openStoryCreator">
          <div class="fb-story-create-img"><user-avatar :src="myAvatar" :name="myName" size="lg" /></div>
          <div class="fb-story-create-foot">
            <div class="fb-story-plus"><i class="mdi mdi-plus"></i></div>
            <span>Create story</span>
          </div>
        </div>
        <div v-for="(g,gi) in stories" :key="g.author_id" class="fb-story" @click="openStoryViewer(gi)">
          <img :src="mediaUrl({url:storyCover(g)})" class="fb-story-bg" />
          <div class="fb-story-avatar" :class="{mine:g.is_mine}"><user-avatar :src="g.author_avatar" :name="g.author_name" size="sm" /></div>
          <div class="fb-story-name">{{ g.is_mine ? 'Your story' : (g.author_name || 'Farmer') }}</div>
        </div>
      </div>

      <!-- COMPOSER -->
      <div class="fb-composer">
        <div class="fb-composer-top">
          <user-avatar :src="myAvatar" :name="myName" size="md" />
          <button class="fb-composer-input" @click="newTopic">{{ myFirst ? "What's on your mind, "+myFirst+"?" : "What's on your mind?" }}</button>
        </div>
        <div class="fb-composer-sep"></div>
        <div class="fb-composer-actions">
          <button class="fb-ca" @click="newTopic"><i class="mdi mdi-image-multiple fb-ca-photo"></i> Photo/Video</button>
          <button class="fb-ca" @click="openStoryCreator"><i class="mdi mdi-camera-iris fb-ca-story"></i> Story</button>
          <button class="fb-ca" @click="newTopicWithLocation"><i class="mdi mdi-map-marker fb-ca-loc"></i> Check in</button>
        </div>
      </div>

      <!-- SCOPE TABS -->
      <div class="fb-feedtabs">
        <button :class="{active:feedScope==='all'}" @click="feedScope='all';loadFeed()">For you</button>
        <button :class="{active:feedScope==='following'}" @click="feedScope='following';loadFeed()">Following</button>
      </div>

      <!-- FOLLOWING LIST -->
      <div v-if="feedScope==='following'" class="fb-following-card">
        <div class="fb-following-head"><i class="mdi mdi-account-heart"></i> People you follow<span v-if="followingUsers.length"> ({{ followingUsers.length }})</span></div>
        <div v-if="followingLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-warning"></div></div>
        <div v-else-if="!followingUsers.length" class="fb-following-empty">You're not following anyone yet. Tap Follow on any post to see their updates here.</div>
        <div v-else class="fb-following-list">
          <div v-for="fu in followingUsers" :key="fu.id" class="fb-following-item">
            <user-avatar :src="fu.avatar_url" :name="fu.name" size="md" style="cursor:pointer" @click="goProfile(fu.id)" />
            <div class="fb-following-body" @click="goProfile(fu.id)">
              <div class="fb-following-name">{{ fu.name && fu.name.trim() ? fu.name : 'A farmer' }}</div>
              <div class="fb-following-meta">{{ Number(fu.followers)||0 }} follower{{ Number(fu.followers)===1?'':'s' }} · {{ Number(fu.posts)||0 }} post{{ Number(fu.posts)===1?'':'s' }}</div>
            </div>
            <button class="fb-follow-btn" @click="unfollowFromList(fu)"><i class="mdi mdi-account-minus"></i> Unfollow</button>
          </div>
        </div>
      </div>

      <!-- FEED -->
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
      <div v-else-if="!posts.length" class="fb-empty">
        <i class="mdi mdi-cow"></i>
        <h5>{{ feedScope==='following' ? 'Nothing from people you follow yet' : 'No posts yet' }}</h5>
        <p>Share how your farm is doing today — feeding, watering, a new calf, anything.</p>
        <button class="btn btn-warning" @click="newTopic"><i class="mdi mdi-plus me-1"></i>Create a post</button>
      </div>
      <div v-else>
        <div v-for="p in posts" :key="p.id" class="fb-post">
          <!-- header -->
          <div class="fb-post-head">
            <user-avatar :src="p.author_avatar" :name="p.author_name" size="md" style="cursor:pointer" @click="goProfile(p.author_id)" />
            <div class="fb-post-head-body">
              <div class="fb-post-author" style="cursor:pointer" @click="goProfile(p.author_id)">
                {{ p.author_name && p.author_name.trim() ? p.author_name : 'A farmer' }}
                <span v-if="p.shared_topic_id" class="fb-post-shared-lbl">shared a post</span>
              </div>
              <div class="fb-post-meta">
                <span>{{ relTime(p.created_at) }}</span>
                <i class="mdi mdi-earth"></i>
                <span v-if="p.location_label" class="fb-post-loc"><i class="mdi mdi-map-marker"></i>{{ p.location_label }}</span>
                <span v-if="p.category && p.category!=='general'" class="fb-post-cat">· {{ p.category }}</span>
              </div>
            </div>
            <button v-if="p.author_id!==myUserId && !p.i_follow" class="fb-follow-btn" @click="toggleFollow(p)"><i class="mdi mdi-plus"></i> Follow</button>
            <div class="fb-menu-wrap" @click.stop>
              <button class="fb-post-menu" @click="menuOpenId = (menuOpenId===p.id?null:p.id)"><i class="mdi mdi-dots-horizontal"></i></button>
              <div v-if="menuOpenId===p.id" class="fb-drop fb-menu-drop">
                <div v-if="p.author_id===myUserId" class="fb-menu-item danger" @click="deletePost(p)"><i class="mdi mdi-delete-outline"></i> Delete post</div>
                <div v-if="p.author_id!==myUserId" class="fb-menu-item" @click="messageAuthor(p)"><i class="mdi mdi-message-text-outline"></i> Message {{ (p.author_name||'').split(' ')[0] || 'farmer' }}</div>
                <div v-if="p.author_id!==myUserId" class="fb-menu-item" @click="toggleFollow(p);menuOpenId=null"><i class="mdi" :class="p.i_follow?'mdi-account-minus':'mdi-account-plus'"></i> {{ p.i_follow?'Unfollow':'Follow' }} {{ (p.author_name||'').split(' ')[0] }}</div>
                <div class="fb-menu-item" @click="p.i_shared ? unshare(p) : openShare(p); menuOpenId=null"><i class="mdi" :class="p.i_shared?'mdi-share-off':'mdi-share'"></i> {{ p.i_shared?'Remove share':'Share' }}</div>
              </div>
            </div>
          </div>

          <!-- body -->
          <div v-if="p.content" class="fb-post-body">{{ p.content }}</div>

          <!-- media -->
          <div v-if="parseMedia(p.photos).length" class="fb-post-media" :class="'n'+Math.min(parseMedia(p.photos).length,4)">
            <template v-for="(m,mi) in parseMedia(p.photos)" :key="mi">
              <img v-if="m.type==='image'||!m.type" :src="mediaUrl(m)" class="fb-media-cell" @click="lightbox=mediaUrl(m)" />
              <video v-else-if="m.type==='video'" :src="mediaUrl(m)" class="fb-media-cell" controls preload="metadata"></video>
              <a v-else :href="mediaUrl(m)" target="_blank" class="fb-post-doc"><i class="mdi" :class="docIcon(m.name)"></i> {{ m.name||'Document' }}</a>
            </template>
          </div>

          <!-- shared embed -->
          <div v-if="p.shared_topic_id && p.shared_content!=null" class="fb-shared" @click="$router.push('/forum/'+p.shared_topic_id)">
            <div class="fb-shared-author"><i class="mdi mdi-share-variant me-1"></i>{{ p.shared_author_name||'A farmer' }}</div>
            <div v-if="p.shared_content" class="fb-shared-body">{{ p.shared_content }}</div>
            <div v-if="parseMedia(p.shared_photos).length" class="fb-shared-media">
              <img v-for="(m,mi) in parseMedia(p.shared_photos).slice(0,2)" :key="mi" :src="mediaUrl(m)" v-show="m.type==='image'||!m.type" />
            </div>
          </div>

          <!-- counts -->
          <div v-if="Number(p.reaction_count)>0 || Number(p.comment_count)>0 || Number(p.share_count)>0" class="fb-post-counts">
            <span v-if="Number(p.reaction_count)>0" class="fb-count-likes"><i class="mdi mdi-thumb-up"></i> {{ p.reaction_count }}</span>
            <span class="fb-count-right">
              <span v-if="Number(p.comment_count)>0" @click="toggleComments(p)">{{ p.comment_count }} comment{{ Number(p.comment_count)>1?'s':'' }}</span>
              <span v-if="Number(p.share_count)>0">{{ p.share_count }} share{{ Number(p.share_count)>1?'s':'' }}</span>
            </span>
          </div>

          <!-- action bar -->
          <div class="fb-post-actions">
            <button class="fb-act" :class="{liked:p.my_reaction}" @click="react(p)"><i class="mdi" :class="p.my_reaction?'mdi-thumb-up':'mdi-thumb-up-outline'"></i> Like</button>
            <button class="fb-act" @click="toggleComments(p)"><i class="mdi mdi-comment-outline"></i> Comment</button>
            <button class="fb-act" :class="{shared:p.i_shared}" @click="p.i_shared ? unshare(p) : openShare(p)"><i class="mdi" :class="p.i_shared?'mdi-share':'mdi-share-outline'"></i> {{ p.i_shared?'Shared':'Share' }}</button>
          </div>

          <!-- comments -->
          <div v-if="p._open" class="fb-comments">
            <div v-for="c in postTop(p)" :key="c.id" class="fb-comment">
              <user-avatar :src="c.author_avatar" :name="c.author_name" size="sm" style="cursor:pointer" @click="goProfile(c.author_id)" />
              <div class="fb-comment-col">
                <div class="fb-comment-bubble">
                  <div class="fb-comment-author">{{ c.author_name && c.author_name.trim() ? c.author_name : 'A farmer' }}</div>
                  <div v-if="c.content" class="fb-comment-text">{{ c.content }}</div>
                </div>
                <div v-if="parseMedia(c.photos).length" class="fb-comment-media">
                  <template v-for="(m,mi) in parseMedia(c.photos)" :key="mi">
                    <img v-if="m.type==='image'||!m.type" :src="mediaUrl(m)" class="fb-comment-img" @click="lightbox=mediaUrl(m)" />
                    <video v-else-if="m.type==='video'" :src="mediaUrl(m)" class="fb-comment-img" controls preload="metadata"></video>
                    <a v-else :href="mediaUrl(m)" target="_blank" class="fb-comment-doc"><i class="mdi" :class="docIcon(m.name)"></i> {{ m.name||'Document' }}</a>
                  </template>
                </div>
                <div class="fb-comment-foot"><span>{{ relTime(c.created_at) }}</span><button class="fb-creply" @click="startPostReply(p,c)">Reply</button></div>
                <div v-for="r in postReplies(p,c.id)" :key="r.id" class="fb-comment fb-comment-reply">
                  <user-avatar :src="r.author_avatar" :name="r.author_name" size="sm" style="cursor:pointer" @click="goProfile(r.author_id)" />
                  <div class="fb-comment-col">
                    <div class="fb-comment-bubble">
                      <div class="fb-comment-author">{{ r.author_name && r.author_name.trim() ? r.author_name : 'A farmer' }}</div>
                      <div v-if="r.content" class="fb-comment-text">{{ r.content }}</div>
                    </div>
                    <div v-if="parseMedia(r.photos).length" class="fb-comment-media">
                      <template v-for="(m,mi) in parseMedia(r.photos)" :key="mi">
                        <img v-if="m.type==='image'||!m.type" :src="mediaUrl(m)" class="fb-comment-img" @click="lightbox=mediaUrl(m)" />
                        <video v-else-if="m.type==='video'" :src="mediaUrl(m)" class="fb-comment-img" controls preload="metadata"></video>
                        <a v-else :href="mediaUrl(m)" target="_blank" class="fb-comment-doc"><i class="mdi" :class="docIcon(m.name)"></i> {{ m.name||'Document' }}</a>
                      </template>
                    </div>
                    <div class="fb-comment-foot"><span>{{ relTime(r.created_at) }}</span><button class="fb-creply" @click="startPostReply(p,c)">Reply</button></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="fb-comment-compose-wrap">
              <div v-if="p._replyTo" class="fb-reply-chip">Replying to <strong>{{ p._replyTo.author_name || 'a farmer' }}</strong><i class="mdi mdi-close" @click="p._replyTo=null"></i></div>
              <div v-if="(p._commentMedia && p._commentMedia.length) || p._commentCompressing" class="fb-comment-previews">
                <div v-for="(m,mi) in (p._commentMedia||[])" :key="mi" class="fb-cpv">
                  <img v-if="m.type==='image'" :src="m.preview" />
                  <video v-else-if="m.type==='video'" :src="m.preview" muted></video>
                  <div v-else class="fb-cpv-doc"><i class="mdi" :class="docIcon(m.name)"></i></div>
                  <button class="fb-cpv-x" type="button" @click="removeCommentMedia(p,mi)"><i class="mdi mdi-close"></i></button>
                </div>
                <span v-if="p._commentCompressing" class="spinner-border spinner-border-sm text-warning" style="margin:auto 4px;"></span>
              </div>
              <div class="fb-comment-compose">
                <user-avatar :src="myAvatar" :name="myName" size="sm" />
                <div class="fb-comment-field">
                  <input v-model="p._commentInput" :placeholder="p._replyTo ? 'Write a reply…' : 'Write a comment…'" @keyup.enter="addComment(p)" />
                  <label class="fb-comment-attach" title="Attach photo, video or file">
                    <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple @change="onCommentMediaPick($event,p)" />
                    <i class="mdi mdi-camera"></i>
                  </label>
                </div>
                <button class="fb-comment-send" @click="addComment(p)" :disabled="p._commentCompressing || !((p._commentInput||'').trim() || (p._commentMedia&&p._commentMedia.length))"><i class="mdi mdi-send"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- RIGHT RAIL -->
    <aside class="fb-right">
      <div class="fb-rail-card">
        <div class="fb-rail-head"><span>People you may know</span></div>
        <div v-if="!suggestions.length" class="fb-rail-empty">No suggestions right now</div>
        <div v-for="u in suggestions" :key="u.id" class="fb-sugg">
          <user-avatar :src="u.avatar_url" :name="u.name" size="md" style="cursor:pointer" @click="goProfile(u.id)" />
          <div class="fb-sugg-body" style="cursor:pointer" @click="goProfile(u.id)">
            <div class="fb-sugg-name">{{ u.name || 'A farmer' }}</div>
            <div class="fb-sugg-meta">{{ Number(u.followers)>0 ? u.followers+' follower'+(Number(u.followers)>1?'s':'') : 'New here' }}</div>
          </div>
          <button class="fb-sugg-follow" :class="{done:u._followed}" @click="followSuggestion(u)">{{ u._followed ? 'Following' : 'Follow' }}</button>
        </div>
      </div>

      <div class="fb-rail-card">
        <div class="fb-rail-head"><span>Market days &amp; events</span><a href="javascript:void(0)" class="fb-rail-link" @click="$router.push('/events')">See all</a></div>
        <div v-if="!upcomingEvents.length" class="fb-rail-empty">No upcoming events yet</div>
        <div v-for="e in upcomingEvents" :key="e.id" class="fb-evt" @click="$router.push('/events/'+e.id)">
          <div class="fb-evt-date"><span class="m">{{ evMon(e.starts_at) }}</span><span class="d">{{ evDay(e.starts_at) }}</span></div>
          <div class="fb-evt-body">
            <div class="fb-evt-title">{{ e.title }}</div>
            <div class="fb-evt-meta">{{ evWhen(e.starts_at) }}<span v-if="e.location_label"> · {{ e.location_label }}</span></div>
            <div v-if="Number(e.going_count)>0" class="fb-evt-going"><i class="mdi mdi-account-check"></i> {{ e.going_count }} going</div>
          </div>
        </div>
        <button class="fb-rail-btn" @click="$router.push('/events')"><i class="mdi mdi-calendar-plus me-1"></i>Create or browse events</button>
      </div>

      <div class="fb-rail-foot">Flockr &copy; {{ new Date().getFullYear() }} · Built for farmers</div>
    </aside>
  </div>

  <!-- ===================== LIGHTBOX ===================== -->
  <div v-if="lightbox" class="fb-lightbox" @click="lightbox=null"><img :src="lightbox" /></div>

  <!-- ===================== STORY VIEWER ===================== -->
  <div v-if="storyViewer.open" class="fb-sv" @click="closeStoryViewer">
    <div class="fb-sv-card" @click.stop>
      <div class="fb-sv-bars">
        <div v-for="(it,ii) in currentStoryItems" :key="ii" class="fb-sv-bar">
          <div class="fb-sv-bar-fill" :style="{width: ii<storyViewer.idx ? '100%' : (ii===storyViewer.idx ? storyViewer.progress+'%' : '0%')}"></div>
        </div>
      </div>
      <div class="fb-sv-head">
        <user-avatar :src="currentStoryGroup.author_avatar" :name="currentStoryGroup.author_name" size="sm" />
        <div class="fb-sv-head-body">
          <div class="fb-sv-name">{{ currentStoryGroup.is_mine ? 'Your story' : currentStoryGroup.author_name }}</div>
          <div class="fb-sv-time">{{ relTime(currentStoryItem.created_at) }}</div>
        </div>
        <button v-if="currentStoryGroup.is_mine" class="fb-sv-x" title="Delete" @click="deleteCurrentStory"><i class="mdi mdi-delete"></i></button>
        <button class="fb-sv-x" @click="closeStoryViewer"><i class="mdi mdi-close"></i></button>
      </div>
      <div class="fb-sv-media">
        <img v-if="currentStoryItem.media_type==='image'" :src="mediaUrl({url:currentStoryItem.media_url})" />
        <video v-else :src="mediaUrl({url:currentStoryItem.media_url})" autoplay playsinline @ended="storyNext"></video>
        <div v-if="currentStoryItem.caption" class="fb-sv-caption">{{ currentStoryItem.caption }}</div>
        <div class="fb-sv-tap fb-sv-tap-l" @click="storyPrev"></div>
        <div class="fb-sv-tap fb-sv-tap-r" @click="storyNext"></div>
      </div>
    </div>
  </div>

  <!-- ===================== CREATE STORY MODAL ===================== -->
  <div v-if="storyForm.open" class="fb-mod-overlay" @click.self="closeStoryCreator">
    <div class="fb-mod-card">
      <h5 class="fb-mod-title"><i class="mdi mdi-camera-iris me-2"></i>Create a story</h5>
      <p class="fb-mod-sub">Share a quick photo or short video of your day. Disappears after 24 hours.</p>

      <label v-if="!storyForm.preview" class="fb-story-drop">
        <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" @change="onStoryPick" />
        <i class="mdi mdi-image-plus"></i>
        <span>Tap to add a photo or video</span>
      </label>
      <div v-else class="fb-story-preview">
        <img v-if="storyForm.type==='image'" :src="storyForm.preview" />
        <video v-else :src="storyForm.preview" controls></video>
        <button class="fb-story-preview-x" @click="clearStoryMedia"><i class="mdi mdi-close"></i></button>
      </div>

      <input v-model="storyForm.caption" class="form-control fb-input mt-2" placeholder="Add a caption (optional)" maxlength="120" />
      <div v-if="storyForm.err" class="text-danger small mt-2">{{ storyForm.err }}</div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="closeStoryCreator" :disabled="storyForm.busy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="submitStory" :disabled="storyForm.busy || !storyForm.file">
          <span v-if="storyForm.busy" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-send me-1"></i>Share story
        </button>
      </div>
    </div>
  </div>

  <!-- ===================== SHARE MODAL ===================== -->
  <div v-if="shareModal.open" class="fb-mod-overlay" @click.self="closeShare">
    <div class="fb-mod-card">
      <h5 class="fb-mod-title"><i class="mdi mdi-share-variant me-2"></i>Share post</h5>

      <!-- external-share row (drives traffic into flockr.africa from social media) -->
      <div class="fb-ext-share">
        <div class="fb-ext-label">Share to</div>
        <div class="fb-ext-row">
          <button v-if="canWebShare" class="fb-ext-btn native" @click="shareNative" title="Share…"><i class="mdi mdi-share-variant"></i><span>Share…</span></button>
          <button class="fb-ext-btn fb" @click="shareTo('facebook')" title="Facebook"><i class="mdi mdi-facebook"></i><span>Facebook</span></button>
          <button class="fb-ext-btn wa" @click="shareTo('whatsapp')" title="WhatsApp"><i class="mdi mdi-whatsapp"></i><span>WhatsApp</span></button>
          <button class="fb-ext-btn tw" @click="shareTo('twitter')" title="X / Twitter"><i class="mdi mdi-twitter"></i><span>X</span></button>
          <button class="fb-ext-btn tg" @click="shareTo('telegram')" title="Telegram"><i class="mdi mdi-telegram"></i><span>Telegram</span></button>
          <button class="fb-ext-btn cp" @click="copyShareLink" title="Copy link"><i class="mdi" :class="copiedFlash?'mdi-check':'mdi-link-variant'"></i><span>{{ copiedFlash?'Copied':'Copy link' }}</span></button>
        </div>
      </div>

      <div class="fb-ext-divider"><span>or share to your Flockr followers</span></div>

      <div class="fb-share-author">
        <user-avatar :src="myAvatar" :name="myName" size="md" />
        <div>
          <div class="fb-share-name">{{ myName }}</div>
          <div class="fb-share-aud"><i class="mdi mdi-account-group"></i> Followers</div>
        </div>
      </div>
      <textarea v-model="shareModal.note" class="form-control fb-input" rows="3" placeholder="Say something about this…"></textarea>
      <div v-if="shareModal.post" class="fb-share-embed">
        <div class="fb-shared-author"><i class="mdi mdi-share-variant me-1"></i>{{ shareModal.post.author_name || 'A farmer' }}</div>
        <div v-if="shareModal.post.content" class="fb-shared-body">{{ shareModal.post.content }}</div>
        <div v-if="parseMedia(shareModal.post.photos).length" class="fb-shared-media">
          <img v-for="(m,mi) in parseMedia(shareModal.post.photos).slice(0,2)" :key="mi" :src="mediaUrl(m)" v-show="m.type==='image'||!m.type" />
        </div>
      </div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="closeShare" :disabled="shareModal.busy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="confirmShare" :disabled="shareModal.busy">
          <span v-if="shareModal.busy" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-share me-1"></i>Share to followers
        </button>
      </div>
    </div>
  </div>

  <!-- ===================== NEW POST MODAL ===================== -->
  <div v-if="showNewModal" class="fb-mod-overlay" @click.self="showNewModal=false">
    <div class="fb-mod-card">
      <h5 class="fb-mod-title"><i class="mdi mdi-pencil me-2"></i>Create a post</h5>
      <div class="fb-share-author">
        <user-avatar :src="myAvatar" :name="myName" size="md" />
        <div>
          <div class="fb-share-name">{{ myName }}</div>
          <select v-model="newForm.category" class="fb-cat-select">
            <option value="general">General</option>
            <option value="health">Health</option>
            <option value="feeding">Feeding</option>
            <option value="breeding">Breeding</option>
            <option value="marketplace">Marketplace</option>
            <option value="equipment">Equipment</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <textarea v-model="newForm.content" class="form-control fb-input fb-post-textarea" rows="4" :placeholder="myFirst ? 'What\'s on your mind, '+myFirst+'? Share how the farm is doing…' : 'Share an update — feeding, watering, a new calf, a disease alert…'"></textarea>

      <label class="fb-lbl">Photos, video &amp; documents <span class="fb-lbl-hint">(optional, up to 6 — photos auto-compressed)</span></label>
      <div class="fp-media-row">
        <template v-for="(m, mi) in newForm.media" :key="mi">
          <div v-if="m.type !== 'document'" class="fp-media-thumb">
            <img v-if="m.type === 'image'" :src="m.preview" />
            <video v-else :src="m.preview" muted></video>
            <button class="fp-media-remove" @click="removeMedia('new', mi)" type="button"><i class="mdi mdi-close"></i></button>
            <span v-if="m.type === 'video'" class="fp-media-badge"><i class="mdi mdi-video"></i></span>
          </div>
          <div v-else class="fp-doc-chip">
            <i class="mdi" :class="docIcon(m.name)" style="font-size:22px;color:#BFA164;"></i>
            <div class="fp-doc-info"><div class="fp-doc-name">{{ m.name || m.file.name }}</div><div class="fp-doc-size">{{ humanSize(m.file ? m.file.size : 0) }}</div></div>
            <button class="fp-doc-remove" @click="removeMedia('new', mi)" type="button"><i class="mdi mdi-close"></i></button>
          </div>
        </template>
        <label v-if="newForm.media.length < 6 && !newForm.compressing" class="fp-media-add">
          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple @change="onMediaPick($event, 'new')" />
          <i class="mdi mdi-paperclip" style="font-size:24px;"></i><span>Add file</span>
        </label>
        <div v-if="newForm.compressing" class="fp-media-add" style="pointer-events:none;">
          <span class="spinner-border text-warning"></span><span style="margin-top:6px;">Compressing...</span>
        </div>
      </div>

      <div class="fp-loc-row">
        <label class="fp-loc-label">
          <input type="checkbox" v-model="newForm.shareLocation" @change="onLocationToggle('new')" />
          <i class="mdi mdi-map-marker me-1" style="color:#50C878;"></i>
          <span v-if="newForm.locationLabel">Posting from <strong style="color:#50C878;">{{ newForm.locationLabel }}</strong></span>
          <span v-else-if="newForm.locationBusy"><span class="spinner-border spinner-border-sm me-1"></span>Detecting your location...</span>
          <span v-else>Share my location with this post</span>
        </label>
        <div v-if="newForm.locationError" style="font-size:11px;color:#FF6B6B;margin-top:4px;">{{ newForm.locationError }}</div>
      </div>

      <div v-if="newError" class="text-danger" style="font-size:12px;margin-top:6px;">{{ newError }}</div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="showNewModal=false" :disabled="newBusy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="submitTopic" :disabled="newBusy || (!newForm.content.trim() && !newForm.media.length)">
          <span v-if="newBusy" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-send me-1"></i>Post
        </button>
      </div>
    </div>
  </div>

</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F0EBE4;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important}
html[data-theme="light"] .fb-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .fb-topbar{background:#FFFFFF!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .fb-post,
html[data-theme="light"] .fb-composer,
html[data-theme="light"] .fb-rail-card,
html[data-theme="light"] .fb-mod-card,
html[data-theme="light"] .fb-drop,
html[data-theme="light"] .fb-story-create{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .fb-search,
html[data-theme="light"] .fb-composer-input,
html[data-theme="light"] .fb-comment-bubble,
html[data-theme="light"] .fb-shared,
html[data-theme="light"] .fb-share-embed,
html[data-theme="light"] .fb-circle,
html[data-theme="light"] .fb-comment-field input,
html[data-theme="light"] .fb-input{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .fb-act:hover,
html[data-theme="light"] .fb-left-item:hover,
html[data-theme="light"] .fb-drop-item:hover{background:#EFEAE3!important}
</style>

<style scoped>
.fb-root{min-height:100vh;background:var(--fm-bg2,#0A0A0A);color:var(--fm-h,#F5F0E8)}

/* ---------- TOP BAR ---------- */
.fb-topbar{position:sticky;top:0;z-index:200;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:8px 14px;background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.fb-tb-left{display:flex;align-items:center;gap:10px;flex:1;min-width:0}
.fb-logo{height:34px}
.fb-search{display:flex;align-items:center;gap:8px;background:var(--fm-bg2,#0F0F0F);border-radius:22px;padding:8px 14px;max-width:240px;flex:1;position:relative}
.fb-search i{color:var(--fm-s,#6B6356);font-size:18px}
.fb-search input{background:transparent;border:none;outline:none;color:var(--fm-h,#F5F0E8);font-size:14px;width:100%}
.fb-tb-center{display:flex;gap:6px}
.fb-nav{width:54px;height:42px;border:none;background:transparent;color:var(--fm-m,#A09888);border-radius:8px;font-size:22px;display:flex;align-items:center;justify-content:center}
.fb-nav:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-nav.active{color:#BFA164;box-shadow:inset 0 -3px 0 #BFA164;border-radius:0}
.fb-tb-right{display:flex;align-items:center;gap:8px;flex:1;justify-content:flex-end}
.fb-iconwrap{position:relative}
.fb-circle{width:40px;height:40px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px;display:flex;align-items:center;justify-content:center;position:relative;padding:0;overflow:visible}
.fb-circle:hover{background:var(--fm-bg3,#1E1E1E)}
.fb-avatar-btn{overflow:hidden}
.fb-badge{position:absolute;top:-3px;right:-3px;min-width:17px;height:17px;padding:0 4px;border-radius:9px;background:#E53935;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid var(--fm-bg1,#141414)}

/* ---------- DROPDOWNS ---------- */
.fb-drop{position:absolute;top:48px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;box-shadow:0 8px 30px rgba(0,0,0,.4);overflow:hidden;z-index:300}
.fb-drop-right{right:0;width:340px;max-height:70vh;overflow-y:auto}
.fb-search-drop{left:0;width:280px;max-height:60vh;overflow-y:auto}
.fb-drop-head{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;font-weight:700;color:var(--fm-h,#F5F0E8);font-size:15px}
.fb-drop-head a{color:#BFA164;font-size:12px;font-weight:600;text-decoration:none}
.fb-drop-empty{padding:24px;text-align:center;color:var(--fm-s,#6B6356);font-size:13px}
.fb-drop-item{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer}
.fb-drop-item:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-drop-item.unread{background:rgba(191,161,100,.06)}
.fb-drop-item-body{flex:1;min-width:0}
.fb-drop-item-title{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-drop-item-sub{color:var(--fm-m,#A09888);font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-drop-item-time{color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px}
.fb-dot{width:10px;height:10px;border-radius:50%;background:#BFA164;flex-shrink:0}
.fb-notif-icon{width:38px;height:38px;border-radius:50%;background:rgba(191,161,100,.12);color:#BFA164;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
.fb-search-item{display:flex;align-items:center;gap:8px;padding:8px 12px}
.fb-search-item:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-search-click{display:flex;align-items:center;gap:8px;flex:1;min-width:0;cursor:pointer}
.fb-search-name{flex:1;min-width:0;color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-mini-follow{background:#BFA164;border:none;color:#0A0A0A;border-radius:14px;padding:4px 10px;font-size:11px;font-weight:700}
.fb-mini-follow.done{background:var(--fm-bg3,#1E1E1E);color:var(--fm-m,#A09888)}

/* ---------- SHELL ---------- */
.fb-shell{display:grid;grid-template-columns:260px minmax(0,640px) 320px;gap:16px;justify-content:center;padding:16px;max-width:1280px;margin:0 auto}
.fb-left,.fb-right{position:sticky;top:66px;align-self:start;max-height:calc(100vh - 80px);overflow-y:auto}
.fb-center{min-width:0}

/* ---------- LEFT RAIL ---------- */
.fb-left-item{display:flex;align-items:center;gap:12px;padding:9px 10px;border-radius:8px;cursor:pointer;color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600}
.fb-left-item:hover{background:var(--fm-bg1,#141414)}
.fb-left-item.active{background:rgba(191,161,100,.12);color:#BFA164}
.fb-left-item.small{font-weight:500;font-size:13px;color:var(--fm-m,#A09888)}
.fb-left-item i{font-size:22px}
.fb-li-msg{color:#4FC3F7}.fb-li-bell{color:#FFB300}.fb-li-follow{color:#EC407A}.fb-li-shop{color:#26A69A}.fb-li-learn{color:#AB47BC}.fb-li-vet{color:#66BB6A}
.fb-left-sep{height:1px;background:var(--fm-br,#1E1E1E);margin:10px 6px}
.fb-left-head{color:var(--fm-s,#6B6356);font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;padding:4px 10px}
.fb-left-foot{color:var(--fm-s,#3A3530);font-size:11px;padding:14px 10px}

/* ---------- STORIES ---------- */
.fb-stories{display:flex;gap:8px;overflow-x:auto;padding-bottom:14px;scrollbar-width:thin}
.fb-story{position:relative;flex:0 0 112px;width:112px;height:200px;border-radius:12px;overflow:hidden;cursor:pointer;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E)}
.fb-story-bg{width:100%;height:100%;object-fit:cover}
.fb-story-avatar{position:absolute;top:8px;left:8px;border-radius:50%;padding:2px;background:var(--fm-bg1,#141414);border:3px solid #1877F2}
.fb-story-avatar.mine{border-color:#BFA164}
.fb-story-name{position:absolute;left:8px;right:8px;bottom:8px;color:#fff;font-size:12px;font-weight:600;text-shadow:0 1px 4px rgba(0,0,0,.8);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-story-create{display:flex;flex-direction:column}
.fb-story-create-img{height:140px;overflow:hidden;display:flex;align-items:center;justify-content:center;background:var(--fm-bg2,#0F0F0F)}
.fb-story-create-img :deep(*){width:100%!important;height:100%!important;border-radius:0!important}
.fb-story-create-foot{flex:1;position:relative;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;padding-bottom:10px}
.fb-story-plus{position:absolute;top:-16px;width:34px;height:34px;border-radius:50%;background:#BFA164;color:#0A0A0A;display:flex;align-items:center;justify-content:center;font-size:22px;border:3px solid var(--fm-bg1,#141414)}
.fb-story-create-foot span{color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600}

/* ---------- COMPOSER ---------- */
.fb-composer{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:12px 14px;margin-bottom:16px}
.fb-composer-top{display:flex;align-items:center;gap:10px}
.fb-composer-input{flex:1;text-align:left;background:var(--fm-bg2,#0F0F0F);border:none;border-radius:22px;padding:10px 16px;color:var(--fm-s,#6B6356);font-size:15px;cursor:pointer}
.fb-composer-input:hover{background:var(--fm-bg3,#1E1E1E)}
.fb-composer-sep{height:1px;background:var(--fm-br,#1E1E1E);margin:10px 0}
.fb-composer-actions{display:flex;gap:4px}
.fb-ca{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;background:transparent;border:none;color:var(--fm-m,#A09888);font-size:14px;font-weight:600;padding:8px;border-radius:8px}
.fb-ca:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-ca i{font-size:22px}
.fb-ca-photo{color:#45BD62}.fb-ca-story{color:#E542A3}.fb-ca-loc{color:#F3425F}

/* ---------- FEED TABS ---------- */
.fb-feedtabs{display:flex;gap:8px;margin-bottom:14px}
.fb-feedtabs button{flex:1;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:10px;padding:9px;font-size:13px;font-weight:700}
.fb-feedtabs button.active{background:#BFA164;color:#0A0A0A;border-color:#BFA164}
.fb-following-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:14px 16px;margin-bottom:14px}
.fb-following-head{color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:700;margin-bottom:10px;display:flex;align-items:center;gap:6px}
.fb-following-head i{color:#BFA164}
.fb-following-empty{color:var(--fm-m,#A09888);font-size:13px;padding:4px 0 2px}
.fb-following-list{display:flex;flex-direction:column;gap:10px}
.fb-following-item{display:flex;align-items:center;gap:10px}
.fb-following-body{flex:1;min-width:0;cursor:pointer}
.fb-following-name{color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fb-following-meta{color:var(--fm-s,#6B6356);font-size:11px}

/* ---------- EMPTY ---------- */
.fb-empty{text-align:center;padding:50px 20px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px}
.fb-empty i{font-size:60px;color:var(--fm-s,#3A3530)}
.fb-empty h5{color:var(--fm-h,#F5F0E8);margin-top:12px}
.fb-empty p{color:var(--fm-s,#6B6356);font-size:14px;margin-bottom:16px}

/* ---------- POST CARD ---------- */
.fb-post{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:14px;margin-bottom:16px}
.fb-post-head{display:flex;align-items:flex-start;gap:10px}
.fb-post-head-body{flex:1;min-width:0}
.fb-post-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.fb-post-shared-lbl{color:var(--fm-m,#A09888);font-weight:400}
.fb-post-meta{display:flex;align-items:center;gap:5px;color:var(--fm-s,#6B6356);font-size:12px;margin-top:1px;flex-wrap:wrap}
.fb-post-meta i{font-size:13px}
.fb-post-loc{display:inline-flex;align-items:center;gap:2px;color:#66BB6A}
.fb-post-cat{text-transform:capitalize}
.fb-follow-btn{background:transparent;border:none;color:#BFA164;font-size:13px;font-weight:700;padding:4px 8px;border-radius:6px;white-space:nowrap;display:flex;align-items:center;gap:2px}
.fb-follow-btn:hover{background:rgba(191,161,100,.1)}
.fb-menu-wrap{position:relative}
.fb-post-menu{width:32px;height:32px;border:none;background:transparent;color:var(--fm-m,#A09888);border-radius:50%;font-size:20px}
.fb-post-menu:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-menu-drop{right:0;top:36px;width:210px;padding:6px}
.fb-menu-item{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:8px;color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;cursor:pointer}
.fb-menu-item:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-menu-item.danger{color:#FF6B6B}
.fb-menu-item i{font-size:18px}
.fb-post-body{color:var(--fm-h,#F5F0E8);font-size:15px;line-height:1.5;white-space:pre-wrap;margin:10px 0}
.fb-post-doc{display:flex;align-items:center;gap:8px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;color:#BFA164;text-decoration:none;font-size:13px;margin-top:8px}
.fb-post-doc .mdi{font-size:24px}

/* media grid */
.fb-post-media{display:grid;gap:3px;margin:10px -14px 0;border-radius:0}
.fb-post-media.n1{grid-template-columns:1fr}
.fb-post-media.n2{grid-template-columns:1fr 1fr}
.fb-post-media.n3{grid-template-columns:1fr 1fr}
.fb-post-media.n3>*:first-child{grid-column:1/-1}
.fb-post-media.n4{grid-template-columns:1fr 1fr}
.fb-media-cell{width:100%;height:100%;max-height:500px;object-fit:cover;background:var(--fm-bg2,#0F0F0F);cursor:pointer;display:block}
.fb-post-media.n1 .fb-media-cell{max-height:560px;object-fit:contain}

/* shared embed */
.fb-shared{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px;margin-top:10px;cursor:pointer}
.fb-shared-author{color:#BFA164;font-size:12px;font-weight:700;margin-bottom:3px}
.fb-shared-body{color:var(--fm-m,#A09888);font-size:13px;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
.fb-shared-media{display:flex;gap:4px;margin-top:8px}
.fb-shared-media img{width:80px;height:80px;object-fit:cover;border-radius:6px}

/* counts + actions */
.fb-post-counts{display:flex;align-items:center;justify-content:space-between;padding:10px 2px;font-size:13px;color:var(--fm-s,#6B6356);border-bottom:1px solid var(--fm-br,#1E1E1E);margin-top:8px}
.fb-count-likes{display:flex;align-items:center;gap:5px}
.fb-count-likes i{color:#1877F2;background:#1877F2;color:#fff;border-radius:50%;font-size:10px;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(#18AFFF,#0062DF)}
.fb-count-right{display:flex;gap:10px}
.fb-count-right span{cursor:pointer}
.fb-count-right span:hover{text-decoration:underline}
.fb-post-actions{display:flex;gap:4px;padding-top:6px}
.fb-act{flex:1;background:transparent;border:none;color:var(--fm-m,#A09888);font-size:14px;font-weight:700;padding:9px;border-radius:8px;display:flex;align-items:center;justify-content:center;gap:8px}
.fb-act:hover{background:var(--fm-bg2,#0F0F0F)}
.fb-act.liked{color:#1877F2}
.fb-act.shared{color:#45BD62}
.fb-act i{font-size:20px}

/* comments */
.fb-comments{border-top:1px solid var(--fm-br,#1E1E1E);margin-top:8px;padding-top:12px}
.fb-comment{display:flex;gap:8px;margin-bottom:10px}
.fb-comment-col{flex:1;min-width:0}
.fb-comment-bubble{background:var(--fm-bg2,#0F0F0F);border-radius:16px;padding:8px 12px;display:inline-block;max-width:100%}
.fb-comment-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px}
.fb-comment-text{color:var(--fm-h,#F5F0E8);font-size:14px;white-space:pre-wrap}
.fb-comment-media{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.fb-comment-img{max-width:180px;max-height:180px;border-radius:12px;object-fit:cover;cursor:pointer}
.fb-comment-doc{display:inline-flex;align-items:center;gap:6px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:8px 10px;color:#BFA164;text-decoration:none;font-size:12px}
.fb-comment-time{color:var(--fm-s,#6B6356);font-size:11px;margin:3px 0 0 12px}
.fb-comment-foot{display:flex;align-items:center;gap:14px;margin:3px 0 0 12px;font-size:11px;color:var(--fm-s,#6B6356)}
.fb-creply{background:none;border:none;color:var(--fm-s,#6B6356);font-weight:700;font-size:12px;padding:0;cursor:pointer}
.fb-creply:hover{color:#BFA164}
.fb-comment-reply{margin-top:10px}
.fb-reply-chip{display:inline-flex;align-items:center;gap:6px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:4px 10px;font-size:12px;color:var(--fm-m,#A09888);align-self:flex-start}
.fb-reply-chip i{cursor:pointer;font-size:15px}
.fb-reply-chip i:hover{color:#FF6B6B}
.fb-comment-compose-wrap{display:flex;flex-direction:column;gap:6px;margin-top:4px}
.fb-comment-previews{display:flex;flex-wrap:wrap;gap:6px;padding-left:40px}
.fb-cpv{position:relative;width:54px;height:54px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E)}
.fb-cpv img,.fb-cpv video{width:100%;height:100%;object-fit:cover}
.fb-cpv-doc{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#BFA164;font-size:22px}
.fb-cpv-x{position:absolute;top:1px;right:1px;width:18px;height:18px;border-radius:50%;background:rgba(0,0,0,.7);border:none;color:#FF6B6B;font-size:11px;display:flex;align-items:center;justify-content:center}
.fb-comment-compose{display:flex;align-items:center;gap:8px}
.fb-comment-field{flex:1;display:flex;align-items:center;background:var(--fm-bg2,#0F0F0F);border-radius:20px;padding:0 10px 0 14px}
.fb-comment-field input{flex:1;background:transparent;border:none;outline:none;color:var(--fm-h,#F5F0E8);font-size:14px;padding:9px 0}
.fb-comment-attach{margin:0;cursor:pointer;color:var(--fm-s,#6B6356);font-size:20px;display:flex}
.fb-comment-attach input{display:none}
.fb-comment-attach:hover{color:#BFA164}
.fb-comment-send{background:transparent;border:none;color:#BFA164;font-size:22px;width:34px;height:34px;flex-shrink:0}
.fb-comment-send:disabled{opacity:.4}

/* ---------- RIGHT RAIL ---------- */
.fb-rail-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:12px 14px;margin-bottom:16px}
.fb-rail-head{display:flex;align-items:center;justify-content:space-between;color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;margin-bottom:10px}
.fb-rail-empty{color:var(--fm-s,#6B6356);font-size:13px;padding:6px 0}
.fb-rail-text{color:var(--fm-m,#A09888);font-size:13px;line-height:1.5;margin-bottom:10px}
.fb-rail-btn{width:100%;background:rgba(191,161,100,.12);border:none;color:#BFA164;font-weight:700;font-size:13px;padding:9px;border-radius:8px}
.fb-rail-btn:hover{background:rgba(191,161,100,.2)}
.fb-rail-foot{color:var(--fm-s,#3A3530);font-size:11px;text-align:center;padding:6px}
.fb-sugg{display:flex;align-items:center;gap:10px;padding:7px 0}
.fb-sugg-body{flex:1;min-width:0}
.fb-sugg-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-sugg-meta{color:var(--fm-s,#6B6356);font-size:11px}
.fb-sugg-follow{background:#BFA164;border:none;color:#0A0A0A;border-radius:8px;padding:6px 14px;font-size:12px;font-weight:700}
.fb-sugg-follow.done{background:var(--fm-bg3,#1E1E1E);color:var(--fm-m,#A09888)}
.fb-rail-link{color:#BFA164;font-size:12px;font-weight:600;text-decoration:none}
.fb-evt{display:flex;gap:10px;padding:8px 0;cursor:pointer;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.fb-evt:hover .fb-evt-title{color:#BFA164}
.fb-evt-date{flex-shrink:0;width:42px;text-align:center;background:var(--fm-bg2,#0F0F0F);border-radius:8px;padding:4px 0;align-self:flex-start}
.fb-evt-date .m{display:block;color:#E5533C;font-size:10px;font-weight:800;text-transform:uppercase}
.fb-evt-date .d{display:block;color:var(--fm-h,#F5F0E8);font-size:16px;font-weight:800;line-height:1}
.fb-evt-body{flex:1;min-width:0}
.fb-evt-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.fb-evt-meta{color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fb-evt-going{color:#45BD62;font-size:11px;font-weight:600;margin-top:2px}
.fb-li-evt{color:#FF7043}

/* ---------- LIGHTBOX ---------- */
.fb-lightbox{position:fixed;inset:0;background:rgba(0,0,0,.92);display:flex;align-items:center;justify-content:center;z-index:2500;padding:20px}
.fb-lightbox img{max-width:100%;max-height:100%;border-radius:8px}

/* ---------- STORY VIEWER ---------- */
.fb-sv{position:fixed;inset:0;background:rgba(0,0,0,.95);display:flex;align-items:center;justify-content:center;z-index:2600}
.fb-sv-card{position:relative;width:380px;max-width:96vw;height:88vh;border-radius:12px;overflow:hidden;background:#000}
.fb-sv-bars{position:absolute;top:8px;left:8px;right:8px;display:flex;gap:4px;z-index:3}
.fb-sv-bar{flex:1;height:3px;border-radius:2px;background:rgba(255,255,255,.35);overflow:hidden}
.fb-sv-bar-fill{height:100%;background:#fff;width:0}
.fb-sv-head{position:absolute;top:18px;left:8px;right:8px;display:flex;align-items:center;gap:8px;z-index:3}
.fb-sv-head-body{flex:1;min-width:0}
.fb-sv-name{color:#fff;font-weight:700;font-size:13px;text-shadow:0 1px 3px rgba(0,0,0,.7)}
.fb-sv-time{color:rgba(255,255,255,.85);font-size:11px}
.fb-sv-x{width:32px;height:32px;border-radius:50%;border:none;background:rgba(0,0,0,.3);color:#fff;font-size:18px}
.fb-sv-media{width:100%;height:100%;position:relative;display:flex;align-items:center;justify-content:center}
.fb-sv-media img,.fb-sv-media video{width:100%;height:100%;object-fit:contain}
.fb-sv-caption{position:absolute;left:0;right:0;bottom:30px;text-align:center;color:#fff;font-size:15px;padding:10px 16px;text-shadow:0 1px 4px rgba(0,0,0,.9)}
.fb-sv-tap{position:absolute;top:0;bottom:0;width:40%;z-index:2}
.fb-sv-tap-l{left:0}.fb-sv-tap-r{right:0}

/* ---------- MODALS ---------- */
.fb-mod-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(4px);z-index:2400;display:flex;align-items:center;justify-content:center;padding:20px}
.fb-mod-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:20px;max-width:520px;width:100%;max-height:92vh;overflow-y:auto}
.fb-mod-title{color:var(--fm-h,#F5F0E8);font-size:18px;font-weight:700;margin-bottom:6px}
.fb-mod-sub{color:var(--fm-s,#6B6356);font-size:13px;margin-bottom:12px}
.fb-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E)!important;color:var(--fm-h,#F5F0E8)!important;font-size:14px}
.fb-input:focus{border-color:#BFA164!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)!important}
.fb-post-textarea{margin-top:10px;border-radius:10px}
.fb-lbl{display:block;color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin:14px 0 4px}
.fb-lbl-hint{color:var(--fm-s,#6B6356);font-size:10px;font-weight:400}
.fb-share-author{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.fb-share-name{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.fb-share-aud{display:inline-flex;align-items:center;gap:4px;background:var(--fm-bg2,#0F0F0F);color:var(--fm-m,#A09888);font-size:11px;padding:2px 8px;border-radius:6px;margin-top:2px}
.fb-cat-select{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:6px;font-size:11px;padding:2px 6px;margin-top:3px}
.fb-share-embed{margin-top:12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px}

/* external share row (drives traffic to flockr.africa from social media) */
.fb-ext-share{margin-bottom:14px}
.fb-ext-label{color:var(--fm-m,#A09888);font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px}
.fb-ext-row{display:flex;flex-wrap:wrap;gap:6px}
.fb-ext-btn{flex:1 1 84px;min-width:72px;display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 6px;border-radius:10px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:11px;font-weight:600;cursor:pointer;transition:transform .08s ease}
.fb-ext-btn:hover{transform:translateY(-1px);border-color:#BFA164}
.fb-ext-btn i{font-size:22px}
.fb-ext-btn.fb i{color:#1877F2}
.fb-ext-btn.wa i{color:#25D366}
.fb-ext-btn.tw i{color:#1DA1F2}
.fb-ext-btn.tg i{color:#2AABEE}
.fb-ext-btn.native i{color:#BFA164}
.fb-ext-btn.cp i{color:#BFA164}
.fb-ext-divider{position:relative;text-align:center;margin:6px 0 14px;font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px}
.fb-ext-divider:before,.fb-ext-divider:after{content:'';position:absolute;top:50%;width:38%;height:1px;background:var(--fm-br,#1E1E1E)}
.fb-ext-divider:before{left:0}.fb-ext-divider:after{right:0}
.fb-ext-divider span{background:var(--fm-bg1,#141414);padding:0 10px;position:relative;z-index:1}
html[data-theme="light"] .fb-ext-divider span{background:#FFFFFF}

/* create-story modal */
.fb-story-drop{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;height:240px;border:2px dashed var(--fm-br,#1E1E1E);border-radius:12px;background:var(--fm-bg2,#0F0F0F);color:var(--fm-s,#6B6356);cursor:pointer}
.fb-story-drop:hover{border-color:#BFA164;color:#BFA164}
.fb-story-drop i{font-size:48px}
.fb-story-drop input{display:none}
.fb-story-preview{position:relative;border-radius:12px;overflow:hidden;background:#000;max-height:300px;display:flex;align-items:center;justify-content:center}
.fb-story-preview img,.fb-story-preview video{max-width:100%;max-height:300px}
.fb-story-preview-x{position:absolute;top:8px;right:8px;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,.6);border:none;color:#fff;font-size:16px}

/* reused composer media grid */
.fp-media-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:6px}
.fp-media-thumb{position:relative;width:84px;height:84px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E)}
.fp-media-thumb img,.fp-media-thumb video{width:100%;height:100%;object-fit:cover}
.fp-media-remove{position:absolute;top:2px;right:2px;width:22px;height:22px;border-radius:50%;background:rgba(0,0,0,.7);border:none;color:#FF6B6B;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.fp-media-badge{position:absolute;bottom:4px;left:4px;background:rgba(0,0,0,.7);color:#FFD700;font-size:10px;padding:1px 6px;border-radius:6px;display:inline-flex;align-items:center;gap:2px}
.fp-media-add{width:84px;height:84px;border-radius:8px;border:2px dashed var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;color:var(--fm-s,#6B6356);font-size:9px;text-align:center;padding:4px}
.fp-media-add:hover{border-color:#BFA164;color:#BFA164}
.fp-media-add input{display:none}
.fp-doc-chip{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);min-width:160px;max-width:240px;height:84px;position:relative}
.fp-doc-info{flex:1;min-width:0}
.fp-doc-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:11px;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fp-doc-size{color:var(--fm-s,#6B6356);font-size:10px}
.fp-doc-remove{position:absolute;top:4px;right:4px;width:20px;height:20px;border-radius:50%;background:rgba(0,0,0,.4);border:none;color:#FF6B6B;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.fp-loc-row{margin-top:14px;padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px}
.fp-loc-label{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:12px;color:var(--fm-m,#A09888);margin:0;line-height:1.4}
.fp-loc-label input[type="checkbox"]{accent-color:#50C878;width:16px;height:16px;cursor:pointer}

/* ---------- RESPONSIVE ---------- */
@media (max-width:1100px){.fb-shell{grid-template-columns:240px minmax(0,640px)}.fb-right{display:none}}
@media (max-width:820px){.fb-shell{grid-template-columns:1fr}.fb-left{display:none}.fb-tb-center{display:none}.fb-search{max-width:160px}}

/* ===== MOBILE DRAWER ===== */
.fb-hamburger{display:none;width:36px;height:36px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0}
html[data-theme="light"] .fb-hamburger{background:#F0EBE4;color:#1A1A1A}
.fb-drawer-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:1099}
.fb-drawer{position:fixed;top:0;left:0;bottom:0;width:280px;max-width:84vw;background:var(--fm-bg1,#141414);border-right:1px solid var(--fm-br,#1E1E1E);z-index:1100;transform:translateX(-100%);transition:transform .25s ease;display:flex;flex-direction:column;overflow:hidden}
html[data-theme="light"] .fb-drawer{background:#FFFFFF;border-right-color:#E2DCD3}
.fb-drawer.open{transform:translateX(0)}
.fb-drawer-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)}
html[data-theme="light"] .fb-drawer-head{border-bottom-color:#E2DCD3}
.fb-drawer-close{width:32px;height:32px;border-radius:50%;border:none;background:transparent;color:var(--fm-s,#6B6356);font-size:18px;cursor:pointer}
.fb-drawer-close:hover{background:rgba(191,161,100,.12);color:#BFA164}
.fb-drawer-body{flex:1;overflow-y:auto;padding:8px 8px 24px}
.fb-drawer-body .fb-left-item{padding:10px 12px;border-radius:8px;font-size:14px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .fb-hamburger{display:inline-flex !important;}
  /* Sticky compact topbar */
  .fb-topbar { padding: 4px 0 !important; }
  .fb-tb { padding: 6px 10px !important; gap: 8px !important; }
  .fb-tb-left { gap: 8px !important; flex: 1 1 auto !important; min-width: 0; }
  .fb-tb-left img { height: 26px !important; }
  .fb-search { max-width: none !important; flex: 1 1 0 !important; min-width: 0; }
  .fb-search input { font-size: 12px !important; padding: 6px 10px !important; }
  .fb-tb-right { gap: 4px !important; }
  .fb-tb-right .fb-icon-btn { width: 34px !important; height: 34px !important; font-size: 18px !important; }

  /* Shell — full width, no rails */
  .fb-shell { padding: 12px 10px calc(40px + env(safe-area-inset-bottom, 0px)) !important; max-width: 100% !important; box-sizing: border-box !important; overflow-x: hidden !important; }
  .fb-center { min-width: 0 !important; }

  /* Stories carousel — smaller tiles, hide "Create story" caption */
  .fb-stories { gap: 6px !important; padding-bottom: 8px !important; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .fb-stories::-webkit-scrollbar { display: none; }
  .fb-story { flex: 0 0 auto !important; width: 72px !important; height: 110px !important; border-radius: 10px !important; }
  .fb-story-create-foot span { font-size: 10px !important; }
  .fb-story-name { font-size: 10px !important; padding: 4px !important; }
  .fb-story-avatar { width: 24px !important; height: 24px !important; }
  .fb-story-plus { width: 22px !important; height: 22px !important; font-size: 14px !important; }

  /* Composer — slimmer, drop "Story" middle action so the row doesn't wrap */
  .fb-composer { padding: 10px 12px !important; border-radius: 10px !important; margin-bottom: 10px !important; }
  .fb-composer-top { gap: 8px !important; }
  .fb-composer-input { font-size: 13px !important; padding: 8px 12px !important; }
  .fb-composer-sep { margin: 8px 0 !important; }
  .fb-composer-actions { gap: 4px !important; }
  .fb-ca { font-size: 11px !important; padding: 6px 4px !important; gap: 3px !important; flex: 1; justify-content: center; }
  .fb-ca i { font-size: 16px !important; }

  /* Feed tabs */
  .fb-feedtabs { gap: 4px !important; margin-bottom: 10px !important; }
  .fb-feedtabs button { font-size: 12px !important; padding: 8px 0 !important; }

  /* Post card */
  .fb-post { padding: 12px !important; border-radius: 10px !important; margin-bottom: 10px !important; }
  .fb-post-head { gap: 8px !important; }
  .fb-post-author { font-size: 13px !important; }
  .fb-post-meta { font-size: 10px !important; gap: 4px !important; flex-wrap: wrap; }
  .fb-post-loc, .fb-post-cat { font-size: 10px !important; }
  .fb-follow-btn { font-size: 10px !important; padding: 4px 8px !important; }
  .fb-post-menu { width: 28px !important; height: 28px !important; font-size: 18px !important; }

  /* Body type */
  .fb-post-body { font-size: 13px !important; line-height: 1.5 !important; margin: 8px 0 !important; }

  /* Media grid — fixed heights so n1..n4 layouts behave */
  .fb-post-media { gap: 2px !important; border-radius: 8px !important; overflow: hidden; }
  .fb-post-media.n1 .fb-media-cell { max-height: 320px !important; }
  .fb-post-media.n2 .fb-media-cell,
  .fb-post-media.n3 .fb-media-cell,
  .fb-post-media.n4 .fb-media-cell { height: 130px !important; }
  .fb-media-cell { object-fit: cover; width: 100%; }

  /* Counts row */
  .fb-post-counts { font-size: 11px !important; padding: 8px 0 6px !important; }

  /* Action bar */
  .fb-post-actions { gap: 4px !important; padding-top: 6px !important; }
  .fb-act { font-size: 12px !important; padding: 8px 6px !important; gap: 4px !important; }
  .fb-act i { font-size: 16px !important; }

  /* Comments under a post */
  .fb-comments { padding-top: 8px !important; gap: 8px !important; }
  .fb-comment { gap: 6px !important; }
  .fb-comment-bubble { padding: 6px 10px !important; }
  .fb-comment-author { font-size: 12px !important; }
  .fb-comment-text { font-size: 12px !important; line-height: 1.45 !important; }
  .fb-comment-foot { font-size: 10px !important; }
  .fb-comment-compose input { font-size: 13px !important; padding: 6px 10px !important; }

  /* Shared embed */
  .fb-shared { padding: 10px !important; border-radius: 8px !important; }
  .fb-shared-author { font-size: 11px !important; }
  .fb-shared-body { font-size: 12px !important; }
}

/* Very tight phones — collapse the centre nav buttons that survived the 820px rule (rare, but defensive) */
@media (max-width: 420px) {
  .fb-tb-right .fb-icon-btn:nth-child(n+3) { display: none; }
  .fb-ca span { display: none; }
  .fb-ca i { margin: 0 !important; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, portal as portalUrl, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

// Shared image compressor — 1200px max, JPEG ~0.8, re-encode to ~0.6 if first pass > 2MB.
function compressImage(file, maxDim, quality) {
  return new Promise(function(resolve){
    var reader = new FileReader();
    reader.onload = function(e){
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else       { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(function(blob){
          if (!blob) { resolve({ file: file, preview: e.target.result }); return; }
          var emit = function(b){
            var name = file.name.replace(/\.[^.]+$/, '.jpg');
            var f = new File([b], name, { type: 'image/jpeg' });
            resolve({ file: f, preview: URL.createObjectURL(f) });
          };
          if (blob.size > 2 * 1024 * 1024) {
            canvas.toBlob(function(b2){ emit(b2 || blob); }, 'image/jpeg', 0.6);
          } else { emit(blob); }
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "ForumPage",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null, loading:false,
    posts: [], feedScope: 'all', lightbox: null,
    followingUsers: [], followingLoading: false,
    filter: { category:'' },
    categories: [
      { key:'health', label:'Animal Health', icon:'mdi-heart-pulse' },
      { key:'feeding', label:'Feeding', icon:'mdi-grain' },
      { key:'breeding', label:'Breeding', icon:'mdi-baby-carriage' },
      { key:'marketplace', label:'Marketplace', icon:'mdi-tag' },
      { key:'equipment', label:'Equipment', icon:'mdi-tractor' }
    ],
    // stories
    stories: [],
    storyViewer: { open:false, gi:0, idx:0, progress:0, timer:null },
    storyForm: { open:false, file:null, preview:null, type:null, caption:'', busy:false, err:'' },
    // chrome
    notifOpen:false, notifs:[], notifUnread:0, notifLoading:false,
    inboxOpen:false, convos:[], inboxUnread:0, inboxLoading:false,
    drawerOpen:false,
    searchQ:'', searchResults:[], searchOpen:false, searchBusy:false, _searchTimer:null,
    suggestions: [],
    upcomingEvents: [],
    menuOpenId: null,
    // modals
    showNewModal: false,
    newForm: { title:'', category:'general', content:'', media: [], compressing: false, shareLocation: false, locationLabel:'', locationLat:null, locationLng:null, locationBusy:false, locationError:'' },
    newBusy: false, newError: '',
    shareModal: { open:false, post:null, note:'', busy:false },
    copiedFlash: false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'},
    myUserId(){return sessionStorage.getItem('userId')||''},
    myName(){return sessionStorage.getItem('userName')||'You'},
    myAvatar(){return sessionStorage.getItem('userAvatar')||''},
    myFirst(){var n=(this.myName||'').trim();return n && n!=='You' ? n.split(' ')[0] : ''},
    currentStoryGroup(){return this.stories[this.storyViewer.gi]||{}},
    currentStoryItems(){return (this.currentStoryGroup.items)||[]},
    currentStoryItem(){return this.currentStoryItems[this.storyViewer.idx]||{}},
    canWebShare(){ return typeof navigator !== 'undefined' && typeof navigator.share === 'function'; },
    shareUrl(){
      var p = this.shareModal.post; if (!p) return '';
      // Branded shortlink — flockr.africa/s/forum/<id>. .htaccess 302-proxies
      // to the API's share.php (rich OG previews) which then redirects humans
      // back to flockr.africa/#/forum/<id>. Shared URL never exposes the API host.
      var root = String(portalUrl || '').replace(/\/+$/, '');
      return root + '/s/forum/' + encodeURIComponent(p.id);
    },
    shareText(){
      var p = this.shareModal.post; if (!p) return '';
      var t = (p.title && p.title !== 'Post') ? p.title : (p.content || '').slice(0, 120);
      return (t ? (t + ' — ') : '') + 'on Flockr';
    }
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this._onDocClick=function(e){
      if(!e.target.closest('.fb-iconwrap')){self.notifOpen=false;self.inboxOpen=false;}
      if(!e.target.closest('.fb-search')){self.searchOpen=false;}
      if(!e.target.closest('.fb-menu-wrap')){self.menuOpenId=null;}
    };
    document.addEventListener('click', this._onDocClick);
    this._onNotifChanged=function(){ if(self.myUserId){ self.loadNotifications(); } };
    window.addEventListener('flockr-notifications-changed', this._onNotifChanged);
    this.loadFeed();
    this.loadStories();
    this.loadSuggestions();
    this.loadUpcomingEvents();
    if(this.myUserId){ this.loadNotifications(); this.loadInbox(); }
  },
  beforeUnmount(){
    if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}
    if(this._onDocClick) document.removeEventListener('click', this._onDocClick);
    if(this._onNotifChanged) window.removeEventListener('flockr-notifications-changed', this._onNotifChanged);
    if(this.storyViewer.timer) clearInterval(this.storyViewer.timer);
  },
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    relTime(ts){
      if(!ts) return '';
      var diff = Math.floor((Date.now() - new Date(ts.replace(' ','T')).getTime()) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return Math.floor(diff/60) + 'm';
      if (diff < 86400) return Math.floor(diff/3600) + 'h';
      if (diff < 2592000) return Math.floor(diff/86400) + 'd';
      return ts.split(' ')[0];
    },
    notifIcon(cat){
      var m={message:'mdi-comment-text',like:'mdi-thumb-up',forum:'mdi-forum',order:'mdi-cart',payment:'mdi-cash',listing:'mdi-tag',system:'mdi-information'};
      return m[cat]||'mdi-bell';
    },
    parseMedia(p){ if(!p)return[]; if(Array.isArray(p))return p; try{var a=JSON.parse(p);return Array.isArray(a)?a:[]}catch(e){return[]} },
    mediaUrl(m){ var u = typeof m==='string'?m:(m&&(m.url||m.path)||''); if(!u)return''; if(u.indexOf('http')===0)return u; return uploads+String(u).replace(/^uploads\//,''); },
    goProfile(id){ if(!id)return; this.searchOpen=false; this.menuOpenId=null; this.$router.push('/u/'+id); },
    messageAuthor(p){
      this.menuOpenId=null;
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      if(!p.author_id || p.author_id===this.myUserId)return;
      window.dispatchEvent(new CustomEvent('flockr-open-chat',{detail:{userId:p.author_id,name:p.author_name||'Farmer',avatar:p.author_avatar||''}}));
    },

    // ---------- FEED ----------
    async loadFeed(){
      this.loading=true;
      if(this.feedScope==='following') this.loadFollowing();
      try{
        var r=await executeJson({function:'getFeed',userId:this.myUserId,scope:this.feedScope,category:this.filter.category,limit:30},flocker);
        if(r.data&&r.data.success){
          var list=((r.data.data&&r.data.data.posts)||[]);
          if(this.filter.category) list=list.filter(function(p){return p.category===this.filter.category}.bind(this));
          this.posts=list.map(function(p){p._open=false;p._comments=[];p._commentInput='';p._commentMedia=[];p._commentCompressing=false;p._replyTo=null;return p;});
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    toggleCategory(key){ this.filter.category = this.filter.category===key ? '' : key; this.loadFeed(); },
    async react(p){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      try{var r=await executeJson({function:'reactToPost',userId:this.myUserId,targetType:'topic',targetId:p.id,reaction:'like'},flocker);
        if(r.data&&r.data.success){p.reaction_count=r.data.data.reactionCount;p.my_reaction=r.data.data.myReaction;}}catch(e){}
    },
    async toggleComments(p){
      p._open=!p._open;
      if(p._open && !(p._comments&&p._comments.length)) await this.loadComments(p);
    },
    async loadComments(p){
      try{var r=await executeJson({function:'getPostComments',topicId:p.id,userId:this.myUserId},flocker);
        if(r.data&&r.data.success)p._comments=(r.data.data&&r.data.data.comments)||[];}catch(e){}
    },
    postTop(p){ return (p._comments||[]).filter(function(c){return !c.parent_post_id;}); },
    postReplies(p,id){ return (p._comments||[]).filter(function(c){return String(c.parent_post_id)===String(id);}); },
    startPostReply(p,c){ p._replyTo=c; },
    async addComment(p){
      var txt=(p._commentInput||'').trim();
      var media=p._commentMedia||[];
      if(!txt && !media.length)return;
      if(p._commentCompressing)return;
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      try{
        var fd=new FormData();
        fd.append('function','addPostComment');
        fd.append('authorId',this.myUserId);
        fd.append('topicId',p.id);
        fd.append('content',txt);
        if(p._replyTo) fd.append('parentPostId',p._replyTo.id);
        var pi=0;
        media.forEach(function(m){if(m.file){fd.append('photo'+pi,m.file);pi++;}});
        var r=await execute(fd,flocker);
        if(r.data&&r.data.success){p._commentInput='';p._commentMedia=[];p._replyTo=null;p.comment_count=(Number(p.comment_count)||0)+1;await this.loadComments(p);}
      }catch(e){}
    },
    async onCommentMediaPick(event,p){
      var files=Array.from(event.target.files||[]);
      if(!p._commentMedia)p._commentMedia=[];
      var slots=4-p._commentMedia.length;
      if(slots<=0){event.target.value='';return;}
      var picked=files.slice(0,slots);
      p._commentCompressing=true;
      try{
        for(var i=0;i<picked.length;i++){
          var f=picked[i];
          if(f.type.indexOf('image/')===0){
            var c=await compressImage(f,1200,0.8);
            p._commentMedia.push({type:'image',file:c.file,preview:c.preview});
          }else if(f.type.indexOf('video/')===0){
            if(f.size>25*1024*1024)continue;
            p._commentMedia.push({type:'video',file:f,preview:URL.createObjectURL(f)});
          }else if(this.isDocFile(f)){
            if(f.size>10*1024*1024)continue;
            p._commentMedia.push({type:'document',file:f,name:f.name,preview:''});
          }
        }
      }catch(e){}
      p._commentCompressing=false;
      event.target.value='';
    },
    removeCommentMedia(p,idx){
      var m=p._commentMedia[idx];
      if(m&&m.preview&&m.preview.indexOf('blob:')===0)URL.revokeObjectURL(m.preview);
      p._commentMedia.splice(idx,1);
    },
    async toggleFollow(p){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      var fn=p.i_follow?'unfollowUser':'followUser';
      try{var r=await executeJson({function:fn,followerId:this.myUserId,followingId:p.author_id},flocker);
        if(r.data&&r.data.success){var nowFollowing=!p.i_follow;
          this.posts.forEach(function(x){if(x.author_id===p.author_id)x.i_follow=nowFollowing?1:null;});
          if(this.feedScope==='following')this.loadFollowing();}}catch(e){}
    },
    async loadFollowing(){
      if(!this.myUserId){this.followingUsers=[];return}
      this.followingLoading=true;
      try{var r=await executeJson({function:'getFollowing',userId:this.myUserId},flocker);
        if(r.data&&r.data.success)this.followingUsers=(r.data.data&&r.data.data.users)||[];}catch(e){}
      this.followingLoading=false;
    },
    async unfollowFromList(fu){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      try{var r=await executeJson({function:'unfollowUser',followerId:this.myUserId,followingId:fu.id},flocker);
        if(r.data&&r.data.success){
          this.followingUsers=this.followingUsers.filter(function(x){return x.id!==fu.id});
          this.posts.forEach(function(x){if(x.author_id===fu.id)x.i_follow=null;});
          this.loadFeed();
        }}catch(e){}
    },
    async deletePost(p){
      this.menuOpenId=null;
      if(!confirm('Delete this post? This cannot be undone.'))return;
      try{var r=await executeJson({function:'deleteFeedPost',userId:this.myUserId,postId:p.id},flocker);
        if(r.data&&r.data.success){this.posts=this.posts.filter(function(x){return x.id!==p.id});}}catch(e){}
    },

    // ---------- SHARE ----------
    // External-share row (FB/WhatsApp/X/Telegram/Copy/native) is open to all
    // viewers — login only enforced for the internal "share to followers" submit.
    openShare(p){
      this.shareModal={open:true,post:p,note:'',busy:false};
      this.copiedFlash=false;
    },
    closeShare(){ this.shareModal={open:false,post:null,note:'',busy:false}; this.copiedFlash=false; },
    async shareNative(){
      if (!this.canWebShare || !this.shareModal.post) return;
      try {
        await navigator.share({ title: 'Flockr', text: this.shareText, url: this.shareUrl });
        this._recordExternalShare('native');
      } catch(e) { /* user cancelled */ }
    },
    shareTo(network){
      if (!this.shareModal.post) return;
      var url = encodeURIComponent(this.shareUrl);
      var text = encodeURIComponent(this.shareText);
      var dest = '';
      if (network === 'facebook') dest = 'https://www.facebook.com/sharer/sharer.php?u=' + url;
      else if (network === 'whatsapp') dest = 'https://wa.me/?text=' + text + '%20' + url;
      else if (network === 'twitter') dest = 'https://twitter.com/intent/tweet?text=' + text + '&url=' + url;
      else if (network === 'telegram') dest = 'https://t.me/share/url?url=' + url + '&text=' + text;
      else return;
      window.open(dest, '_blank', 'noopener,noreferrer,width=600,height=600');
      this._recordExternalShare(network);
    },
    async copyShareLink(){
      if (!this.shareModal.post) return;
      var link = this.shareUrl;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          var ta = document.createElement('textarea');
          ta.value = link; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        }
        this.copiedFlash = true;
        var self = this; setTimeout(function(){ self.copiedFlash = false; }, 1500);
        this._recordExternalShare('copy');
      } catch(e) {}
    },
    _recordExternalShare(network){
      try {
        var p = this.shareModal.post; if (!p || !p.id) return;
        executeJson({function:'logForumExternalShare', topicId:p.id, userId:this.myUserId, network:network}, flocker).catch(function(){});
      } catch(e){}
    },
    async confirmShare(){
      var p=this.shareModal.post; if(!p)return;
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      this.shareModal.busy=true;
      try{
        var orig=p.shared_topic_id||p.id;
        var r=await executeJson({function:'createFeedPost',authorId:this.myUserId,content:this.shareModal.note,sharedTopicId:orig},flocker);
        if(r.data&&r.data.success){
          this.posts.forEach(function(x){if((x.shared_topic_id||x.id)==orig){x.i_shared=1;x.share_count=(Number(x.share_count)||0)+1;}});
          this.closeShare();
          this.loadFeed();
        }else{ this.shareModal.busy=false; }
      }catch(e){ this.shareModal.busy=false; }
    },
    async unshare(p){
      if(!this.myUserId)return;
      var orig=p.shared_topic_id||p.id;
      try{var r=await executeJson({function:'unshareTopic',userId:this.myUserId,topicId:orig},flocker);
        if(r.data&&r.data.success){
          this.posts.forEach(function(x){if((x.shared_topic_id||x.id)==orig){x.i_shared=null;x.share_count=Math.max(0,(Number(x.share_count)||1)-1);}});
          this.loadFeed();
        }}catch(e){}
    },

    // ---------- STORIES ----------
    async loadStories(){
      try{var r=await executeJson({function:'getStories',userId:this.myUserId},flocker);
        if(r.data&&r.data.success)this.stories=(r.data.data&&r.data.data.stories)||[];}catch(e){}
    },
    storyCover(g){ return (g.items&&g.items.length)?g.items[g.items.length-1].media_url:''; },
    openStoryViewer(gi){
      this.storyViewer.open=true; this.storyViewer.gi=gi; this.storyViewer.idx=0;
      this._startStoryTimer();
    },
    closeStoryViewer(){
      this.storyViewer.open=false;
      if(this.storyViewer.timer){clearInterval(this.storyViewer.timer);this.storyViewer.timer=null;}
    },
    _startStoryTimer(){
      var self=this;
      if(this.storyViewer.timer)clearInterval(this.storyViewer.timer);
      this.storyViewer.progress=0;
      var dur=5000, step=50;
      this.storyViewer.timer=setInterval(function(){
        self.storyViewer.progress += (step/dur)*100;
        if(self.storyViewer.progress>=100){ self.storyNext(); }
      }, step);
    },
    storyNext(){
      var items=this.currentStoryItems;
      if(this.storyViewer.idx < items.length-1){ this.storyViewer.idx++; this._startStoryTimer(); }
      else if(this.storyViewer.gi < this.stories.length-1){ this.storyViewer.gi++; this.storyViewer.idx=0; this._startStoryTimer(); }
      else { this.closeStoryViewer(); }
    },
    storyPrev(){
      if(this.storyViewer.idx>0){ this.storyViewer.idx--; this._startStoryTimer(); }
      else if(this.storyViewer.gi>0){ this.storyViewer.gi--; this.storyViewer.idx=(this.stories[this.storyViewer.gi].items||[]).length-1; this._startStoryTimer(); }
    },
    async deleteCurrentStory(){
      var it=this.currentStoryItem; if(!it.id)return;
      if(!confirm('Delete this story?'))return;
      try{await executeJson({function:'deleteStory',userId:this.myUserId,storyId:it.id},flocker);}catch(e){}
      this.closeStoryViewer();
      this.loadStories();
    },
    openStoryCreator(){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      this.storyForm={open:true,file:null,preview:null,type:null,caption:'',busy:false,err:''};
    },
    closeStoryCreator(){ this.clearStoryMedia(); this.storyForm.open=false; },
    clearStoryMedia(){
      if(this.storyForm.preview && this.storyForm.preview.indexOf('blob:')===0) URL.revokeObjectURL(this.storyForm.preview);
      this.storyForm.file=null; this.storyForm.preview=null; this.storyForm.type=null;
    },
    async onStoryPick(e){
      var f=(e.target.files||[])[0]; e.target.value='';
      if(!f)return;
      this.storyForm.err='';
      if(f.type.indexOf('image/')===0){
        var c=await compressImage(f,1280,0.8);
        this.storyForm.file=c.file; this.storyForm.preview=c.preview; this.storyForm.type='image';
      }else if(f.type.indexOf('video/')===0){
        if(f.size>25*1024*1024){this.storyForm.err='Videos must be under 25 MB.';return;}
        this.storyForm.file=f; this.storyForm.preview=URL.createObjectURL(f); this.storyForm.type='video';
      }else{ this.storyForm.err='Stories support photos and videos only.'; }
    },
    async submitStory(){
      if(!this.storyForm.file)return;
      this.storyForm.busy=true; this.storyForm.err='';
      try{
        var fd=new FormData();
        fd.append('function','createStory');
        fd.append('authorId',this.myUserId);
        fd.append('caption',this.storyForm.caption);
        fd.append('photo0',this.storyForm.file);
        var r=await execute(fd,flocker);
        if(r.data&&r.data.success){ this.closeStoryCreator(); this.loadStories(); }
        else{ this.storyForm.err=(r.data&&r.data.message)||'Could not post story'; this.storyForm.busy=false; }
      }catch(e){ this.storyForm.err='Network error'; this.storyForm.busy=false; }
    },

    // ---------- NOTIFICATIONS ----------
    async loadNotifications(){
      this.notifLoading=true;
      try{var r=await executeJson({function:'getNotifications',userId:this.myUserId,limit:15},flocker);
        if(r.data&&r.data.success){this.notifs=(r.data.data&&r.data.data.notifications)||[];this.notifUnread=Number(r.data.data.unread)||0;}}catch(e){}
      this.notifLoading=false;
    },
    toggleNotif(){ this.notifOpen=!this.notifOpen; this.inboxOpen=false; if(this.notifOpen) this.loadNotifications(); },
    async openNotif(n){
      this.notifOpen=false;
      try{await executeJson({function:'markNotificationRead',notificationId:n.id,userId:this.myUserId},flocker);}catch(e){}
      if(Number(n.is_read)===0) this.notifUnread=Math.max(0,this.notifUnread-1);
      if(n.action_url){ this.$router.push(n.action_url); }
    },
    async markAllNotifs(){
      try{await executeJson({function:'markNotificationRead',userId:this.myUserId},flocker);}catch(e){}
      this.notifs.forEach(function(n){n.is_read=1;}); this.notifUnread=0;
    },

    // ---------- INBOX ----------
    async loadInbox(){
      this.inboxLoading=true;
      try{var r=await executeJson({function:'getConversations',userId:this.myUserId},flocker);
        if(r.data&&r.data.success){this.convos=(r.data.data&&r.data.data.conversations)||[];
          this.inboxUnread=this.convos.reduce(function(s,c){return s+(Number(c.unread_count)||0)},0);}}catch(e){}
      this.inboxLoading=false;
    },
    toggleInbox(){ this.inboxOpen=!this.inboxOpen; this.notifOpen=false; if(this.inboxOpen) this.loadInbox(); },
    openConvo(c){ this.inboxOpen=false; this.$router.push('/messages/'+c.id); },

    // ---------- SEARCH ----------
    onSearch(){
      var self=this; this.searchOpen=true;
      if(this._searchTimer)clearTimeout(this._searchTimer);
      if((this.searchQ||'').trim().length<2){ this.searchResults=[]; this.searchBusy=false; return; }
      this.searchBusy=true;
      this._searchTimer=setTimeout(function(){ self.doSearch(); }, 300);
    },
    async doSearch(){
      try{var r=await executeJson({function:'searchUsers',q:this.searchQ.trim(),limit:8},flocker);
        if(r.data&&r.data.success)this.searchResults=((r.data.data&&r.data.data.users)||[]).filter(function(u){return u.id!==this.myUserId}.bind(this));}catch(e){}
      this.searchBusy=false;
    },
    async followFromSearch(u){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      var fn=u._followed?'unfollowUser':'followUser';
      try{var r=await executeJson({function:fn,followerId:this.myUserId,followingId:u.id},flocker);
        if(r.data&&r.data.success)u._followed=!u._followed;}catch(e){}
    },

    // ---------- SUGGESTIONS ----------
    async loadSuggestions(){
      try{var r=await executeJson({function:'getFollowSuggestions',userId:this.myUserId,limit:6},flocker);
        if(r.data&&r.data.success)this.suggestions=(r.data.data&&r.data.data.users)||[];}catch(e){}
    },
    async followSuggestion(u){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      var fn=u._followed?'unfollowUser':'followUser';
      try{var r=await executeJson({function:fn,followerId:this.myUserId,followingId:u.id},flocker);
        if(r.data&&r.data.success){u._followed=!u._followed;
          this.posts.forEach(function(x){if(x.author_id===u.id)x.i_follow=u._followed?1:null;});}}catch(e){}
    },

    // ---------- EVENTS (rail) ----------
    async loadUpcomingEvents(){
      try{var r=await executeJson({function:'getEvents',userId:this.myUserId,when:'upcoming',limit:3},flocker);
        if(r.data&&r.data.success)this.upcomingEvents=(r.data.data&&r.data.data.events)||[];}catch(e){}
    },
    _evDate(ts){ return ts ? new Date(String(ts).replace(' ','T')) : null; },
    evMon(ts){ var d=this._evDate(ts); return d?d.toLocaleDateString('en',{month:'short'}):''; },
    evDay(ts){ var d=this._evDate(ts); return d?d.getDate():''; },
    evWhen(ts){ var d=this._evDate(ts); return d?d.toLocaleDateString('en',{weekday:'short',day:'numeric',month:'short'})+' · '+d.toLocaleTimeString('en',{hour:'numeric',minute:'2-digit'}):''; },

    // ---------- COMPOSER ----------
    newTopic(){
      if(!this.myUserId){this.$router.push('/login?redirect=/forum');return}
      this.newForm={title:'',category:'general',content:'',media:[],compressing:false,shareLocation:true,locationLabel:'',locationLat:null,locationLng:null,locationBusy:false,locationError:''};
      this.newError='';
      this.showNewModal=true;
      var self=this;
      this.$nextTick(function(){ self.onLocationToggle('new'); });
    },
    newTopicWithLocation(){ this.newTopic(); },
    onLocationToggle(target){
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      if (!form.shareLocation) { form.locationLabel=''; form.locationLat=null; form.locationLng=null; form.locationError=''; return; }
      if (!navigator.geolocation) { form.shareLocation=false; form.locationError = 'Geolocation not supported on this browser'; return; }
      form.locationBusy = true; form.locationError = '';
      navigator.geolocation.getCurrentPosition(async function(pos){
        form.locationLat = pos.coords.latitude; form.locationLng = pos.coords.longitude;
        try {
          var url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + form.locationLat + '&lon=' + form.locationLng + '&zoom=8';
          var resp = await fetch(url, { headers: { 'Accept-Language': 'en' } });
          var data = await resp.json();
          var a = data.address || {};
          var parts = [a.city || a.town || a.village || a.county || a.state_district || a.state, a.country];
          form.locationLabel = parts.filter(Boolean).join(', ') || data.display_name || ('GPS ' + form.locationLat.toFixed(2) + ', ' + form.locationLng.toFixed(2));
        } catch(e) { form.locationLabel = 'GPS ' + form.locationLat.toFixed(2) + ', ' + form.locationLng.toFixed(2); }
        form.locationBusy = false;
      }, function(err){
        form.locationBusy = false; form.shareLocation = false;
        form.locationError = err && err.code === 1 ? 'Permission denied' : 'Could not detect location';
      }, { timeout: 8000, enableHighAccuracy: false });
    },
    async onMediaPick(event, target){
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      var files = Array.from(event.target.files || []);
      var slots = 6 - form.media.length;
      if (slots <= 0) { event.target.value=''; return; }
      var picked = files.slice(0, slots);
      form.compressing = true;
      try {
        for (var i = 0; i < picked.length; i++) {
          var f = picked[i];
          if (f.type.indexOf('image/') === 0) {
            var c = await compressImage(f, 1200, 0.8);
            form.media.push({ type:'image', file: c.file, preview: c.preview });
          } else if (f.type.indexOf('video/') === 0) {
            if (f.size > 25 * 1024 * 1024) { this.newError = 'Videos must be under 25 MB.'; continue; }
            form.media.push({ type:'video', file: f, preview: URL.createObjectURL(f) });
          } else if (this.isDocFile(f)) {
            if (f.size > 10 * 1024 * 1024) { this.newError = 'Documents must be under 10 MB.'; continue; }
            form.media.push({ type:'document', file: f, name: f.name, preview: '' });
          } else { this.newError = 'Unsupported file type: ' + (f.name || 'file'); }
        }
      } catch(e){ this.newError = 'Could not process file: ' + (e && e.message); }
      form.compressing = false;
      event.target.value = '';
    },
    isDocFile(f){
      var allowedMime = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/vnd.ms-powerpoint','application/vnd.openxmlformats-officedocument.presentationml.presentation','text/plain','text/csv'];
      var allowedExt = /\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i;
      return allowedMime.indexOf(f.type) > -1 || allowedExt.test(f.name || '');
    },
    docIcon(name){
      var n = (name || '').toLowerCase();
      if (n.indexOf('.pdf') > -1) return 'mdi-file-pdf-box';
      if (/\.docx?$/.test(n)) return 'mdi-file-word-box';
      if (/\.xlsx?$/.test(n) || n.indexOf('.csv') > -1) return 'mdi-file-excel-box';
      if (/\.pptx?$/.test(n)) return 'mdi-file-powerpoint-box';
      if (n.indexOf('.txt') > -1) return 'mdi-file-document-outline';
      return 'mdi-file-outline';
    },
    humanSize(b){
      b = Number(b||0); if (b < 1024) return b + ' B';
      if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB';
      return (b/1024/1024).toFixed(1) + ' MB';
    },
    removeMedia(target, idx){
      var form = target === 'new' ? this.newForm : null;
      if (!form) return;
      var m = form.media[idx];
      if (m && m.preview && m.preview.indexOf('blob:') === 0) URL.revokeObjectURL(m.preview);
      form.media.splice(idx, 1);
    },
    async submitTopic(){
      this.newBusy=true; this.newError='';
      try{
        var fd = new FormData();
        fd.append('function', 'createFeedPost');
        fd.append('authorId', this.myUserId);
        fd.append('content', this.newForm.content);
        fd.append('category', this.newForm.category);
        if(this.newForm.shareLocation && this.newForm.locationLabel){
          fd.append('locationLabel', this.newForm.locationLabel);
          if(this.newForm.locationLat!=null) fd.append('locationLat', this.newForm.locationLat);
          if(this.newForm.locationLng!=null) fd.append('locationLng', this.newForm.locationLng);
        }
        var pi = 0;
        this.newForm.media.forEach(function(m){ if (m.file) { fd.append('photo' + pi, m.file); pi++; } });
        var r = await execute(fd, flocker);
        if(r.data&&r.data.success){ this.showNewModal=false; this.loadFeed(); }
        else{ this.newError=(r.data&&r.data.message)||'Could not post'; }
      }catch(e){ this.newError=(e&&e.message)||'Network error'; }
      this.newBusy=false;
    }
  }
};
</script>
